import { CmsContentRaw } from '@parcel2go/npm-p2g-app';
import { ReactNode } from 'react';
import { findContentEntry, useCmsContentGroup } from '~/hooks/use-cms-content-group';

interface props {
  /** The CMS content group to fetch, e.g. `ContentGroups.PAYMENTS`. */
  group: string;
  /** The `id` or `name` field of the entry to render. */
  contentKey: string;
  /** Fallback rendered when the entry is not published. */
  children?: ReactNode;
  /** Wraps whichever of the entry or the fallback renders. */
  wrapper?: (content: ReactNode) => ReactNode;
}

/**
 * Renders a CMS entry from a content group fetched independently of the current route.
 *
 * Mirrors `CmsContent`'s contract, but sources its entries from `useCmsContentGroup` rather than the
 * route-scoped `ContentContext` - use it for content inside side panels, which open over any route.
 *
 * Renders nothing while loading: these slots are decorative, so they must not hold up or shift the
 * surrounding panel.
 */
export default function StandaloneCmsContent({ group, contentKey, children, wrapper }: props) {
  const { entries, isLoading } = useCmsContentGroup(group);

  if (isLoading) {
    return null;
  }

  const entry = findContentEntry(entries, contentKey);

  if (!entry) {
    return <>{children && wrapper ? wrapper(children) : children}</>;
  }

  return <>{wrapper ? wrapper(CmsContentRaw(entry)) : CmsContentRaw(entry)}</>;
}
